import React, { useEffect, useMemo, useState } from "react";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { ArrowLeft2 as ArrowLeftIcon, ArrowRight2 as ArrowRightIcon } from "iconsax-react";
import { CustomerPortalShipment } from "Models/customerPortalShipment";
import { useTranslation } from "react-i18next";
import { ShipmentType } from "Util/enums";

import {
  Card,
  CardContent,
  CardContentWrapper,
  CardText,
  CardTextContent,
  CardTitle,
  Container,
  IconButton,
} from "./styled";

const PLANE_ICON = require("Assets/img/plane_icon.svg");
const SHIP_ICON = require("Assets/img/ship-marker-2.svg");

const libraries: ("drawing" | "geometry" | "places" | "visualization")[] = ["places"];

interface SummaryLivePositionMapProps {
  shipments: CustomerPortalShipment[];
  googleMapsApiKey: string;
  minHeight?: number;
}

export const SummaryLivePositionMap = (props: SummaryLivePositionMapProps) => {
  const { googleMapsApiKey, shipments, minHeight } = props;

  const [currentShipmentIndex, setCurrentShipmentIndex] = useState<number>(0);
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (!shipments.length) return;

    setCurrentShipmentIndex(0);
  }, [shipments]);

  const hasMoreThanOneShipment = useMemo(() => {
    return shipments.length > 1;
  }, [shipments]);

  const currentShipment = useMemo<CustomerPortalShipment | undefined>(() => {
    return shipments.at(currentShipmentIndex ?? 0);
  }, [shipments, currentShipmentIndex]);

  const hasMoreThanOneContainers = useMemo(() => {
    if (!currentShipment?.containers?.length) return 0;

    return currentShipment.containers.length > 1;
  }, [shipments]);

  const positionCenter = useMemo(() => {
    if (!currentShipment) {
      return { lat: 0, lng: 0 };
    }

    return {
      lat: Number(currentShipment.shipmentTracking.vesselPosition!.latitude),
      lng: Number(currentShipment.shipmentTracking.vesselPosition!.longitude),
    };
  }, [currentShipment]);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const { isLoaded } = useJsApiLoader({
    libraries,
    googleMapsApiKey,
  });

  if (!isLoaded) return <></>;

  const getIconConfig = (shipment: CustomerPortalShipment) => {
    return (
      iconsDictionary[shipment.shipmentType as keyof typeof iconsDictionary] ??
      iconsDictionary[ShipmentType.SEA]
    );
  };

  const iconsDictionary = {
    [ShipmentType.AIR]: {
      url: PLANE_ICON,
      anchor: new google.maps.Point(0, 32),
      scaledSize: new google.maps.Size(27, 32),
    },
    [ShipmentType.SEA]: {
      url: SHIP_ICON,
      anchor: new google.maps.Point(0, 32),
      scaledSize: new google.maps.Size(32, 32),
    },
  };

  const renderItems = () => {
    return shipments.map((shipment) => {
      const position = {
        lat: Number(shipment.shipmentTracking.vesselPosition!.latitude),
        lng: Number(shipment.shipmentTracking.vesselPosition!.longitude),
      };

      return <Marker key={shipment.id} icon={getIconConfig(shipment)} position={position} />;
    });
  };

  const renderContainersTitle = () => {
    return hasMoreThanOneContainers ? translate("containers") : translate("container");
  };

  const renderContainersText = () => {
    if (!currentShipment?.containers?.length) return "-";

    return currentShipment.containers
      .map((container) => {
        return container.id;
      })
      .join(", ");
  };

  const renderCurrentShipment = () => {
    if (!currentShipment) return;

    return (
      <Card elevation={0}>
        <CardContent>
          {hasMoreThanOneShipment && (
            <IconButton
              size={matches ? "medium" : "small"}
              disabled={currentShipmentIndex <= 0}
              onClick={() => {
                const nextIndex = currentShipmentIndex - 1;

                setCurrentShipmentIndex(nextIndex <= 0 ? 0 : nextIndex);
              }}
            >
              <ArrowLeftIcon />
            </IconButton>
          )}

          <CardContentWrapper>
            <CardTitle>
              {translate("shipment")} {currentShipmentIndex + 1}
            </CardTitle>

            <CardText>
              {translate("billOfLading")}:
              <CardTextContent>{currentShipment.billOfLanding}</CardTextContent>
            </CardText>

            <CardText>
              {renderContainersTitle()}: <CardTextContent>{renderContainersText()}</CardTextContent>
            </CardText>
          </CardContentWrapper>

          {hasMoreThanOneShipment && (
            <IconButton
              size={matches ? "medium" : "small"}
              disabled={currentShipmentIndex >= shipments.length - 1}
              onClick={() => {
                const nextIndex = currentShipmentIndex + 1;

                setCurrentShipmentIndex(
                  nextIndex >= shipments.length ? shipments.length : nextIndex
                );
              }}
            >
              <ArrowRightIcon />
            </IconButton>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Container>
      <GoogleMap
        zoom={3}
        center={positionCenter}
        options={{
          minZoom: 2,
          maxZoom: 15,
          disableDefaultUI: true,
          zoomControl: false,
        }}
        mapContainerStyle={{
          flex: 1,
          borderRadius: matches ? theme.shape.borderRadius : undefined,
          minHeight: minHeight || (matches ? "590px" : "360px"),
          marginLeft: matches ? undefined : "-24px",
          marginRight: matches ? undefined : "-24px",
        }}
      >
        {renderItems()}
        {matches && renderCurrentShipment()}
      </GoogleMap>

      {!matches && renderCurrentShipment()}
    </Container>
  );
};
